/**
 * TP-Link Omada SDN + JetStream + Easy-Smart + Unmanaged PoE catalog.
 *
 * All specs verified against tp-link.com / omadanetworks.com product pages
 * on 2026-05-15. URL cited per entry. Missing specs left undefined rather
 * than guessed.
 *
 * Naming note: several legacy "TL-SG..." SKUs are End-of-Sale; TP-Link
 * has shipped successor SKUs without the "TL-" prefix (SG2210MP, SG2218P,
 * SG2428P, SG3428MP, SG3210XHP-M2, SG3452P, SG3452XP, SG3428XMP). The
 * current shipping SKU is used as `model`; legacy SKUs were skipped.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const TPLINK_OMADA_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Omada SDN — Smart-Managed (L2)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'tplink-sg2008p',
    manufacturer: 'TP-Link',
    model: 'SG2008P',
    category: 'poe-switch',
    tagline: 'Omada 8-port Gigabit smart switch with 4 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2008p/',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 62,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-sg2016p',
    manufacturer: 'TP-Link',
    model: 'SG2016P',
    category: 'poe-switch',
    tagline: 'Omada 16-port Gigabit smart switch with 8 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2016p/',
    specs: {
      totalPorts: 16,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 32,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-sg2210p',
    manufacturer: 'TP-Link',
    model: 'SG2210P',
    category: 'poe-switch',
    tagline: 'Omada 10-port Gigabit smart switch with 8 PoE+ (fanless)',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2210p/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 61,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-sg2210mp',
    manufacturer: 'TP-Link',
    model: 'SG2210MP',
    category: 'poe-switch',
    tagline: 'Omada 10-port Gigabit smart switch with 8 PoE+, 150 W budget',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2210mp/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg2218p',
    manufacturer: 'TP-Link',
    model: 'SG2218P',
    category: 'poe-switch',
    tagline: 'Omada 18-port Gigabit smart switch with 16 PoE+, fanless',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2218p/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      switchingCapacityGbps: 36,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  {
    id: 'tplink-sg2428p',
    manufacturer: 'TP-Link',
    model: 'SG2428P',
    category: 'poe-switch',
    tagline: 'Omada 28-port Gigabit smart switch with 24 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2428p/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg2428lp',
    manufacturer: 'TP-Link',
    model: 'SG2428LP',
    category: 'poe-switch',
    tagline: 'Omada 28-port Gigabit smart switch with 16 PoE+ (fanless)',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2428lp/',
    specs: {
      totalPorts: 28,
      poePorts: 16,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  {
    id: 'tplink-sg2452lp',
    manufacturer: 'TP-Link',
    model: 'SG2452LP',
    category: 'poe-switch',
    tagline: 'Omada 52-port Gigabit smart switch with 32 PoE+ (fanless)',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg2452lp/',
    specs: {
      totalPorts: 52,
      poePorts: 32,
      poeBudgetW: 230,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 104,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Omada SDN — L2+ Managed (10G SFP+ uplinks, JetStream tier)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'tplink-sg3210xhp-m2',
    manufacturer: 'TP-Link',
    model: 'SG3210XHP-M2',
    category: 'poe-switch',
    tagline: 'Omada 8× 2.5G PoE+ L2+ managed switch with 2× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access-pro/sg3210xhp-m2/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP+ 10G',
      switchingCapacityGbps: 80,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg3428mp',
    manufacturer: 'TP-Link',
    model: 'SG3428MP',
    category: 'poe-switch',
    tagline: 'Omada 28-port Gigabit L2+ managed switch with 24 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg3428mp/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 384,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg3428xmp',
    manufacturer: 'TP-Link',
    model: 'SG3428XMP',
    category: 'poe-switch',
    tagline: 'Omada 24× Gigabit + 4× 10G SFP+ L2+ managed with 24 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access-plus/sg3428xmp/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 384,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg3452p',
    manufacturer: 'TP-Link',
    model: 'SG3452P',
    category: 'poe-switch',
    tagline: 'Omada 52-port Gigabit L2+ managed switch with 48 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/sg3452p/',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 384,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      switchingCapacityGbps: 104,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-sg3452xp',
    manufacturer: 'TP-Link',
    model: 'SG3452XP',
    category: 'poe-switch',
    tagline: 'Omada 48× Gigabit + 4× 10G SFP+ L2+ managed with 48 PoE+',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access-plus/sg3452xp/',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 500,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP+ 10G',
      switchingCapacityGbps: 176,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Easy-Smart (web-managed lite)
  // ─────────────────────────────────────────────────────────────

  {
    id: 'tplink-tl-sg105pe',
    manufacturer: 'TP-Link',
    model: 'TL-SG105PE',
    category: 'poe-switch',
    tagline: '5-port Gigabit Easy Smart with 4 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sg105pe/',
    specs: {
      totalPorts: 5,
      poePorts: 4,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 10,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-tl-sg108pe',
    manufacturer: 'TP-Link',
    model: 'TL-SG108PE',
    category: 'poe-switch',
    tagline: '8-port Gigabit Easy Smart with 4 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/easy-smart-switch/tl-sg108pe/',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 64,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-tl-sg1016pe',
    manufacturer: 'TP-Link',
    model: 'TL-SG1016PE',
    category: 'poe-switch',
    tagline: '16-port Gigabit Easy Smart with 8 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/easy-smart-switch/tl-sg1016pe/',
    specs: {
      totalPorts: 16,
      poePorts: 8,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 32,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tp-link-tl-sg1218mpe',
    manufacturer: 'TP-Link',
    model: 'TL-SG1218MPE',
    category: 'poe-switch',
    tagline: '18-port Gigabit Easy Smart PoE+ with 2 SFP combo',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sg1218mpe/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP (combo)',
      switchingCapacityGbps: 36,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Unmanaged PoE+
  // ─────────────────────────────────────────────────────────────

  {
    id: 'tplink-tl-sg1005p',
    manufacturer: 'TP-Link',
    model: 'TL-SG1005P',
    category: 'poe-switch',
    tagline: '5-port Gigabit unmanaged with 4 PoE',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sg1005p/',
    specs: {
      totalPorts: 5,
      poePorts: 4,
      poeBudgetW: 56,
      poeStandard: '802.3af',
      poePerPortW: 15.4,
      switchingCapacityGbps: 10,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tp-link-tl-sg1008p',
    manufacturer: 'TP-Link',
    model: 'TL-SG1008P',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged with 4 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sg1008p/',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 64,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-tl-sg1008pe',
    manufacturer: 'TP-Link',
    model: 'TL-SG1008PE',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged with 8 PoE+ (rackmount)',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/unmanaged-switch/tl-sg1008pe/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 126,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  {
    id: 'tplink-tl-sg1218mp',
    manufacturer: 'TP-Link',
    model: 'TL-SG1218MP',
    category: 'poe-switch',
    tagline: '18-port Gigabit unmanaged rackmount with 16 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/soho-switch-unmanaged/tl-sg1218mp/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP (combo)',
      switchingCapacityGbps: 36,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // Fast-Ethernet unmanaged PoE (cheap entry-level)
  {
    id: 'tplink-tl-sf1006p',
    manufacturer: 'TP-Link',
    model: 'TL-SF1006P',
    category: 'poe-switch',
    tagline: '6-port 10/100M unmanaged with 4 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sf1006p/',
    specs: {
      totalPorts: 6,
      poePorts: 4,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 1.2,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  {
    id: 'tplink-tl-sf1009p',
    manufacturer: 'TP-Link',
    model: 'TL-SF1009P',
    category: 'poe-switch',
    tagline: '9-port 10/100M unmanaged with 8 PoE+',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/poe-switch/tl-sf1009p/',
    specs: {
      totalPorts: 9,
      poePorts: 8,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 1.8,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },
];
